import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setActiveSchedule } from '@store/slices/activeSchedule.slice';
import {
  readLastViewedSchedule,
  writeLastViewedSchedule,
} from '@components/Pages/SchedulerPage/lib/lastViewedSchedule';

/** Minimal shape a schedule list needs for active-schedule resolution. */
interface IHasScheduleNumber {
  scheduleNumber: string;
}

/**
 * Resolve which schedule a site should show: the explicitly-selected one (from
 * Redux, else the persisted last-viewed), validated against the loaded list, and
 * falling back to the most-recent schedule (first) when there's no valid choice.
 * Pure so it can be reused outside React (tests, the picker's trigger label).
 */
export function resolveActiveScheduleNumber(
  selected: string | null | undefined,
  schedules: readonly IHasScheduleNumber[],
): string | undefined {
  if (selected && schedules.some((s) => s.scheduleNumber === selected)) return selected;
  return schedules[0]?.scheduleNumber;
}

/**
 * The user's raw schedule selection for `siteId` — Redux first (the TopBar
 * switch), then the persisted last-viewed — WITHOUT validating it against a
 * loaded list. Callers that have the list use `useActiveScheduleNumber` (which
 * validates + falls back to newest); callers that don't (e.g. the Portfolio,
 * which lets the backend degrade a stale pin) read this directly. Reactive.
 */
export function useSelectedScheduleNumber(siteId: string | undefined): string | null {
  const fromRedux = useAppSelector((state) => (siteId ? state.activeSchedule.bySite[siteId] : undefined));
  return fromRedux ?? (siteId ? readLastViewedSchedule(siteId) : null);
}

/**
 * The active schedule for `siteId`, resolved against the given (loaded) list.
 * Reactive: re-resolves when the user switches via the TopBar picker. Reads the
 * raw selection (Redux, then persisted last-viewed) and validates it against the
 * list, falling back to the newest schedule when there's no valid choice.
 */
export function useActiveScheduleNumber(
  siteId: string | undefined,
  schedules: readonly IHasScheduleNumber[],
): string | undefined {
  const selected = useSelectedScheduleNumber(siteId);
  return resolveActiveScheduleNumber(selected, schedules);
}

/**
 * Set the active schedule for a site — updates the reactive store and persists
 * the choice. Every schedule-aware surface reads this, so the switch is global.
 */
export function useSetActiveSchedule(): (siteId: string, scheduleNumber: string) => void {
  const dispatch = useAppDispatch();
  return useCallback(
    (siteId: string, scheduleNumber: string) => {
      if (!siteId || !scheduleNumber) return;
      dispatch(setActiveSchedule({ siteId, scheduleNumber }));
      writeLastViewedSchedule(siteId, scheduleNumber);
    },
    [dispatch],
  );
}
